import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import SmartConnectorService from 'services/SmartConnectorService';
import { SMART_CONNECTOR_STATUSES } from 'pages/SmartConnectors/constants';
import type {
  FlowLoadErrors,
  RunSmartConnectorFlowLoad,
  SmartConnector,
  SmartConnectorFlowErrors,
  SmartConnectorFlowLoad,
  SmartConnectorStatus,
} from 'pages/SmartConnectors/types';
import { isCSVLoadStep } from 'pages/SmartConnectors/SmartConnector/helpers';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import useModal from '__components/Modals/useModal';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import Button from '__components/Button';
import { FlowBuilderContext } from '../context';
import { useScrollMenuIntoView } from 'hooks/useScrollMenuIntoView';
import { SmartConnectorSettings } from './sections/SmartConnectorSettings';
import { ErrorHandling } from './sections/ErrorHandling';
import { SourceSettings } from './sections/SourceSettings';
import Loader from '__components/Kizen/Loader';
import { ReturnScopes } from './sections/ReturnScopes';

const getData = (
  initialData: Partial<RunSmartConnectorFlowLoad>,
  index: number
): RunSmartConnectorFlowLoad => ({
  type: 'run_smartconnector',
  smart_connector_to_run: null,
  wait_for_execution: true,
  error_handling: {
    on_zero_records: 'skip',
    if_inactive: 'queue',
    on_error: 'ignore',
    on_child_cancelled: 'continue',
  },
  input_data: {
    scope: undefined,
    variable: undefined,
    filter: undefined,
  },
  order: index,
  return_scopes: [],
  ...initialData,
});

const errorsOrder: Partial<Record<keyof FlowLoadErrors, number>> = {
  smart_connector_to_run: 0,
  input_data: 1,
  error_handling: 2,
  return_scopes: 3,
};

export const RunSmartConnectorStepModal = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const {
    smartConnector,
    dropStep,
    droppedStep,
    setSmartConnector,
    showDeactivationModalWithResult,
    flowErrors,
    setFlowErrors,
    uploads,
  } = useContext(FlowBuilderContext);

  const [data, setData] = useState<RunSmartConnectorFlowLoad>(() =>
    getData(droppedStep?.step.details, droppedStep?.step.order || 0)
  );
  const [initialData] = useState<RunSmartConnectorFlowLoad>(() => data);
  const [saving, setSaving] = useState(false);

  const index = droppedStep?.index || 0;
  const isNew = !data?.id;

  const [loadErrors, setLoadErrors] = useState<FlowLoadErrors>(() => {
    return isNew ? {} : flowErrors.flow.loads?.[index] || {};
  });

  const { value: smartConnectorToRun, loading: loadingSmartConnectorToRun } =
    useAsync(async () => {
      if (!data.smart_connector_to_run) {
        return undefined;
      }
      const response = await SmartConnectorService.getSmartConnector(
        data.smart_connector_to_run
      );
      return response.data as SmartConnector;
    }, [data.smart_connector_to_run]);

  const isDirty = useMemo(() => {
    return !isEqual(data, initialData);
  }, [initialData, data]);

  const [confirmationModalProps, , { show: showConfirmationModal }] = useModal({
    handleSubmit: () => {
      dropStep(null);
    },
  });

  const onHide = useCallback(() => {
    if (!isDirty) {
      dropStep(null);
    } else {
      showConfirmationModal();
    }
  }, [dropStep, isDirty, showConfirmationModal]);

  const handleSave = useCallback(
    async (status: SmartConnectorStatus) => {
      try {
        setLoadErrors({});
        setSaving(true);

        const payload: SmartConnectorFlowLoad = {
          id: isNew ? undefined : data.id,
          type: 'run_smartconnector',
          smart_connector_to_run: data.smart_connector_to_run,
          wait_for_execution: data.wait_for_execution,
          error_handling: data.error_handling,
          input_data: data.input_data,
          return_scopes: data.wait_for_execution ? data.return_scopes : [],
          order: index,
        };

        const loads = [...(uploads || []).map((load) => ({ ...load.details }))];
        if (isNew) {
          loads.splice(index, 0, payload);
        }

        const response: { data: SmartConnector } =
          await SmartConnectorService.updateSmartConnector(smartConnector.id, {
            flow: {
              loads: loads.map((load, i) =>
                i === index
                  ? payload
                  : {
                      id: load.id,
                      order: i,
                      ...(isCSVLoadStep(load)
                        ? { custom_object: load.custom_object }
                        : {}),
                    }
              ),
            },
            status,
          });

        setSmartConnector(response.data);

        const loadErrors = [...(flowErrors.flow.loads || [])];

        if (loadErrors.length) {
          setFlowErrors((prev) => ({
            ...prev,
            flow: {
              ...prev.flow,
              loads: isNew
                ? loadErrors.toSpliced(index, 0, {})
                : loadErrors.map((err, i) => (i === index ? {} : err || {})),
            },
          }));
        }

        dropStep(null);
        return true;
      } catch (error) {
        if (error instanceof AxiosError) {
          const axiosError = (
            error as AxiosError<{ flow: SmartConnectorFlowErrors }>
          ).response?.data;

          const loadError = axiosError?.flow?.loads?.[index] ?? {};

          if (Object.values(loadError).length) {
            setLoadErrors(loadError);
          } else {
            showToast({
              variant: toastVariant.FAILURE,
              message: t(
                'An error occurred while saving the step. Please try again.'
              ),
            });
          }
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      smartConnector,
      setSmartConnector,
      setFlowErrors,
      dropStep,
      index,
      data,
      isNew,
      showToast,
      flowErrors,
      uploads,
      t,
    ]
  );

  const onConfirm = useCallback(() => {
    if (
      isDirty &&
      getIsSmartConnectorActive({
        status: smartConnector.status,
      })
    ) {
      return showDeactivationModalWithResult({
        handleSave: () => handleSave(SMART_CONNECTOR_STATUSES.inactive),
        handleDiscard: () => {
          setData(initialData);
          return null;
        },
      });
    } else {
      return handleSave(smartConnector.status);
    }
  }, [
    isDirty,
    handleSave,
    showDeactivationModalWithResult,
    smartConnector.status,
    initialData,
  ]);

  const resetError = useCallback(
    (
      patch:
        | Partial<FlowLoadErrors>
        | ((prev: FlowLoadErrors) => Partial<FlowLoadErrors>)
    ) => {
      setLoadErrors((prev) => ({
        ...prev,
        ...(typeof patch === 'function' ? patch(prev) : patch),
      }));
    },
    []
  );

  const firstErrorKey = useMemo(() => {
    const firstErrorKey = (Object.keys(loadErrors) as (keyof FlowLoadErrors)[])
      .sort((a, b) => (errorsOrder[a] || 0) - (errorsOrder[b] || 0))
      .find(
        (key) =>
          loadErrors[key] &&
          (key !== 'return_scopes' ||
            loadErrors.return_scopes?.some(
              (err) => Object.values(err).filter(Boolean).length
            )) &&
          (!['input_data', 'error_handling'].includes(key) ||
            Object.values(loadErrors[key] || {}).filter(Boolean).length)
      );
    return firstErrorKey;
  }, [loadErrors]);

  const isValid = useMemo(
    () =>
      Boolean(
        data.smart_connector_to_run &&
          data.input_data.scope &&
          data.input_data.variable &&
          data.return_scopes.every((scope) => scope.name && scope.source)
      ),
    [
      data.smart_connector_to_run,
      data.input_data.scope,
      data.input_data.variable,
      data.return_scopes,
    ]
  );

  const sectionRef = useScrollMenuIntoView();

  return (
    <>
      <BasicModal
        show
        size="medium"
        heading={
          isNew
            ? t('Add Run SmartConnector Step')
            : t('Edit Run SmartConnector Step')
        }
        buttonText={t('Save & Close')}
        defaultLeftBtnText={t('Cancel')}
        onConfirm={onConfirm}
        onHide={onHide}
        disabled={!isValid || saving || !!firstErrorKey}
      >
        <div ref={sectionRef}>
          <SmartConnectorSettings
            smartConnectorToRunId={data.smart_connector_to_run}
            smartConnectorToRunName={smartConnectorToRun?.name}
            waitForExecution={data.wait_for_execution}
            onChange={setData}
            error={
              firstErrorKey === 'smart_connector_to_run'
                ? loadErrors.smart_connector_to_run
                : undefined
            }
            resetError={resetError}
          />
          <Loader loading={loadingSmartConnectorToRun}>
            {smartConnectorToRun ? (
              <>
                <SourceSettings
                  loadId={data.id}
                  smartConnectorToRun={smartConnectorToRun}
                  inputData={data.input_data}
                  onChange={setData}
                  errors={loadErrors.input_data}
                  showError={firstErrorKey === 'input_data'}
                  resetError={resetError}
                />
                <ErrorHandling
                  smartConnectorToRun={smartConnectorToRun}
                  waitForExecution={data.wait_for_execution}
                  errorHandling={data.error_handling}
                  onChange={setData}
                  errors={loadErrors.error_handling}
                  showError={firstErrorKey === 'error_handling'}
                  resetError={resetError}
                />
                {data.wait_for_execution ? (
                  <ReturnScopes
                    loadId={data.id}
                    smartConnectorToRun={smartConnectorToRun}
                    returnScopes={data.return_scopes}
                    onChange={setData}
                    showError={firstErrorKey === 'return_scopes'}
                    errors={loadErrors.return_scopes}
                    resetError={resetError}
                  />
                ) : null}
              </>
            ) : null}
          </Loader>
        </div>
      </BasicModal>
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Save Changes')}
        actionBtnColor="red"
        leftBtn={<Button disabled={!isValid || saving || !!firstErrorKey} />}
        defaultLeftBtnHandler={onConfirm}
        {...confirmationModalProps}
        disabled={saving}
      >
        {t(
          'You have unsaved changes. Would you like to save them or discard them before continuing?'
        )}
      </ConfirmationModal>
    </>
  );
};
